import React from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Timer, RefreshCw, Pause, Play, CheckCircle } from 'lucide-react';
import { useLiveEvent } from './LiveEventContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const EventControlBar = ({ onOpenFinishModal }) => {
  const {
    t, currentSession, currentStatus, eventElapsedTime,
    onResetSession, onRefresh
  } = useLiveEvent();

  const formatElapsed = (sec) => {
    if (!sec && sec !== 0) return '00:00:00';
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = Math.floor(sec % 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  if (!currentSession || !currentStatus) return null;

  const isPaused = !!currentStatus.session?.is_paused;

  const handlePauseResume = async () => {
    try {
      const action = isPaused ? 'resume' : 'pause';
      await axios.post(`${API_BASE_URL}/api/event-sessions/${currentSession}/${action}`);
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error('Pause/Resume failed:', e);
    }
  };

  return (
    <Card className={`bg-gradient-to-br ${isPaused ? 'from-amber-900/60 to-yellow-900/40 border-amber-600/50' : 'from-green-900/50 to-emerald-900/40 border-green-700/40'}`} data-testid="event-control-bar">
      <CardContent className="py-3 px-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Timer size={18} className={isPaused ? 'text-amber-400' : 'text-green-400'} />
              <span className={`text-xs font-medium ${isPaused ? 'text-amber-300' : 'text-green-300'}`}>{t('eventElapsed') || 'Event Elapsed'}</span>
            </div>
            <span className="text-white font-mono font-bold text-xl" data-testid="event-elapsed-time">
              {formatElapsed(eventElapsedTime)}
            </span>
            {isPaused && (
              <Badge className="bg-amber-700/60 text-amber-100 text-xs animate-pulse" data-testid="paused-badge">
                <Pause size={10} className="mr-1" /> {t('paused') || 'PAUSED'}
              </Badge>
            )}
            {currentStatus.session && (
              <Badge className="bg-green-700/50 text-green-200 text-xs">
                {currentStatus.session.name || currentStatus.session.event_type_code}
              </Badge>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                if (window.confirm(t('confirmRestartEvent') || 'Restart the event? All performances will be cleared and timers will restart from now.')) {
                  onResetSession && onResetSession(currentSession);
                }
              }}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              size="sm"
              data-testid="restart-event-btn"
            >
              <RefreshCw className="mr-1.5" size={14} />
              {t('restartEvent') || 'Restart Event'}
            </Button>
            <Button
              onClick={handlePauseResume}
              className={isPaused ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'}
              size="sm"
              data-testid={isPaused ? 'resume-event-btn' : 'pause-event-btn'}
            >
              {isPaused ? <Play className="mr-1.5" size={14} /> : <Pause className="mr-1.5" size={14} />}
              {isPaused ? (t('resumeEvent') || 'Resume Event') : (t('pauseEvent') || t('stopEvent') || 'Pause Event')}
            </Button>
            <Button
              onClick={() => onOpenFinishModal && onOpenFinishModal(currentSession)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              size="sm"
              data-testid="finish-event-btn"
            >
              <CheckCircle className="mr-1.5" size={14} />
              {t('finishEvent') || 'Finish Event'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EventControlBar;
